import { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { fetchAuthSession } from 'aws-amplify/auth';
import mqtt from 'mqtt';
import { signIoTUrl } from '../lib/sigv4';
import type { Device, DeviceTelemetry } from '../types';

const IOT_ENDPOINT = import.meta.env.VITE_IOT_ENDPOINT ?? '';
const AWS_REGION = import.meta.env.VITE_COGNITO_USER_POOL_ID?.split('_')[0] ?? 'ap-southeast-2';

// AWS IoT Core hard limit is 50 subscriptions per connection.
// 3 topics per device (telemetry/status/faults) → cap at 40 to stay under 50.
const MAX_SUBSCRIPTIONS = 40;
const MAX_VISIBLE_DEVICES = Math.floor(MAX_SUBSCRIPTIONS / 3); // 13
const DEBOUNCE_MS = 300;
const UNSUBSCRIBE_DELAY_MS = 2000; // hysteresis: avoid thrash on fast scroll

interface MqttState {
  isConnected: boolean;
  telemetry: Map<string, DeviceTelemetry>;
  observeRow: (serial: string) => (el: HTMLElement | null) => void;
}

function topicsFor(serial: string, customer: string): string[] {
  return [
    `kinetica/${customer}/${serial}/telemetry`,
    `kinetica/${customer}/${serial}/status`,
    `kinetica/${customer}/${serial}/faults`,
  ];
}

export function useMqtt(devices: Device[]): MqttState {
  const [isConnected, setIsConnected] = useState(false);
  const [telemetry, setTelemetry] = useState<Map<string, DeviceTelemetry>>(() => new Map());
  const clientRef = useRef<mqtt.MqttClient | null>(null);
  const telemetryRef = useRef<Map<string, DeviceTelemetry>>(new Map());

  // Viewport-scoping state (large-fleet path only)
  const visibleSerialsRef = useRef<Set<string>>(new Set());
  const subscribedSerialsRef = useRef<Set<string>>(new Set());
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const unsubscribeTimersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());
  const observerRef = useRef<IntersectionObserver | null>(null);
  const rowElementsRef = useRef<Map<string, HTMLElement>>(new Map());

  const deviceSerials = useMemo(() => devices.map(a => a.serial), [devices]);
  const isLargeFleet = deviceSerials.length > MAX_VISIBLE_DEVICES;

  // Reconcile subscriptions to match the current visible set (large-fleet path).
  // Called after debounce fires; diffs against subscribedSerialsRef.
  const reconcile = useCallback(() => {
    const client = clientRef.current;
    if (!client || !client.connected) return;

    // Determine the target set: visible serials capped at MAX_VISIBLE_DEVICES
    const visible = [...visibleSerialsRef.current];
    const target = new Set(visible.slice(0, MAX_VISIBLE_DEVICES));

    const subscribed = subscribedSerialsRef.current;

    // Subscribe to newly visible devices
    for (const serial of target) {
      // Always cancel a pending unsubscribe timer when the device is visible
      const pendingTimer = unsubscribeTimersRef.current.get(serial);
      if (pendingTimer !== undefined) {
        clearTimeout(pendingTimer);
        unsubscribeTimersRef.current.delete(serial);
      }

      if (!subscribed.has(serial)) {
        const device = devices.find(a => a.serial === serial);
        if (device) {
          client.subscribe(topicsFor(serial, device.org_id));
          subscribed.add(serial);
        }
      }
    }

    // Schedule unsubscribe for devices that left the viewport (with hysteresis)
    for (const serial of subscribed) {
      if (!target.has(serial) && !unsubscribeTimersRef.current.has(serial)) {
        const timer = setTimeout(() => {
          if (clientRef.current?.connected) {
            const device = devices.find(a => a.serial === serial);
            if (device) {
              clientRef.current.unsubscribe(topicsFor(serial, device.org_id));
            }
          }
          subscribedSerialsRef.current.delete(serial);
          unsubscribeTimersRef.current.delete(serial);
        }, UNSUBSCRIBE_DELAY_MS);
        unsubscribeTimersRef.current.set(serial, timer);
      }
    }

    if (import.meta.env.DEV) {
      const subCount = subscribedSerialsRef.current.size * 3;
      console.debug(`[MQTT] ${subCount}/${MAX_SUBSCRIPTIONS} subscriptions`);
    }
  }, []);

  const scheduleReconcile = useCallback(() => {
    if (debounceTimerRef.current !== null) clearTimeout(debounceTimerRef.current);
    debounceTimerRef.current = setTimeout(reconcile, DEBOUNCE_MS);
  }, [reconcile]);

  // Build (or tear down) the IntersectionObserver for large-fleet mode.
  useEffect(() => {
    if (!isLargeFleet) {
      if (observerRef.current) {
        observerRef.current.disconnect();
        observerRef.current = null;
      }
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          const serial = (entry.target as HTMLElement).dataset.mqttSerial;
          if (!serial) continue;
          if (entry.isIntersecting) {
            visibleSerialsRef.current.add(serial);
          } else {
            visibleSerialsRef.current.delete(serial);
          }
        }
        scheduleReconcile();
      },
      { rootMargin: '0px', threshold: 0.1 },
    );

    observerRef.current = observer;

    // Observe any rows already registered
    for (const [, el] of rowElementsRef.current) {
      observer.observe(el);
    }

    return () => {
      observer.disconnect();
      observerRef.current = null;
    };
  }, [isLargeFleet, scheduleReconcile]);

  const connect = useCallback(async () => {
    if (deviceSerials.length === 0) return;
    if (!IOT_ENDPOINT && import.meta.env.MODE !== 'test') return;

    if (clientRef.current) {
      clientRef.current.end(true);
      clientRef.current = null;
    }

    try {
      const session = await fetchAuthSession();
      const creds = session.credentials;
      if (!creds) {
        console.warn('[MQTT] No credentials available — identity pool not configured?');
        return;
      }

      const identityId = session.identityId;
      if (!identityId) {
        console.warn('[MQTT] No identity ID — cannot form client ID');
        return;
      }
      const clientId = `operator-${identityId}`;

      const url = await signIoTUrl(IOT_ENDPOINT, AWS_REGION, {
        accessKeyId: creds.accessKeyId,
        secretAccessKey: creds.secretAccessKey,
        sessionToken: creds.sessionToken,
      });

      const client = mqtt.connect(url, {
        clientId,
        protocolVersion: 4,
        reconnectPeriod: 5000,
        connectTimeout: 10_000,
        transformWsUrl: (_url, _opts, _client) => url,
      });

      clientRef.current = client;

      client.on('connect', () => {
        console.log('[MQTT] Connected to', IOT_ENDPOINT);
        setIsConnected(true);

        if (!isLargeFleet) {
          // Small-fleet fast path: subscribe to all devices immediately
          subscribedSerialsRef.current = new Set(deviceSerials);
          for (const device of devices) {
            client.subscribe(topicsFor(device.serial, device.org_id));
          }
          if (import.meta.env.DEV) {
            console.debug(`[MQTT] ${deviceSerials.length * 3}/${MAX_SUBSCRIPTIONS} subscriptions`);
          }
        } else {
          // Large-fleet path: only subscribe to currently visible devices
          subscribedSerialsRef.current = new Set();
          reconcile();
        }
      });

      client.on('message', (topic, payload) => {
        try {
          const data = JSON.parse(payload.toString());
          const parts = topic.split('/');
          // Topic format: kinetica/{customer}/{serial}/{channel}
          const serial = parts[2];
          const channel = parts[3];

          if (channel === 'telemetry') {
            telemetryRef.current.set(serial, {
              serial,
              timestamp: data.timestamp ?? new Date().toISOString(),
              joint_positions: data.joint_positions ?? [],
              joint_temperatures: data.joint_temperatures ?? [],
              cycle_time_ms: data.cycle_time_ms ?? 0,
              pick_success_rate: data.pick_success_rate ?? 0,
              cpu_temp: data.cpu_temp ?? 0,
              power_watts: data.power_watts ?? 0,
            });
            setTelemetry(new Map(telemetryRef.current));
          }
        } catch {
          // Ignore malformed payloads
        }
      });

      client.on('error', (err) => {
        console.error('[MQTT] Error:', err);
      });

      client.on('close', () => {
        console.log('[MQTT] Disconnected');
        setIsConnected(false);
      });

      client.on('reconnect', () => {
        console.log('[MQTT] Reconnecting...');
      });
    } catch (err) {
      console.error('[MQTT] Connection setup failed:', err);
      setIsConnected(false);
    }
  }, [deviceSerials, isLargeFleet, reconcile]);

  useEffect(() => {
    connect();

    return () => {
      // Clear all pending timers
      if (debounceTimerRef.current !== null) clearTimeout(debounceTimerRef.current);
      for (const timer of unsubscribeTimersRef.current.values()) clearTimeout(timer);
      unsubscribeTimersRef.current.clear();
      visibleSerialsRef.current.clear();
      subscribedSerialsRef.current.clear();

      if (clientRef.current) {
        clientRef.current.end(true);
        clientRef.current = null;
      }
      setIsConnected(false);
    };
  }, [connect]);

  // Returns a ref callback to attach to a <tr> row for IntersectionObserver tracking.
  // For small fleets (≤13 devices) this is a no-op ref.
  const observeRow = useCallback(
    (serial: string) =>
      (el: HTMLElement | null) => {
        if (!isLargeFleet) return;

        const existing = rowElementsRef.current.get(serial);

        if (el === null) {
          // Row unmounted — unobserve and clean up
          if (existing) observerRef.current?.unobserve(existing);
          rowElementsRef.current.delete(serial);
          visibleSerialsRef.current.delete(serial);
          return;
        }

        if (existing === el) return; // same element, nothing to do

        if (existing) observerRef.current?.unobserve(existing);
        el.dataset.mqttSerial = serial;
        rowElementsRef.current.set(serial, el);
        observerRef.current?.observe(el);
      },
    [isLargeFleet],
  );

  return { isConnected, telemetry, observeRow };
}
